import { useCallback, useEffect, useRef, useState } from "react";

interface ScrollSyncOptions {
  offsetTop?: number;
  throttleMs?: number;
}

interface ScrollSyncResult {
  activeDateString: string | undefined;
  scrollToDate: (dateString: string, skipPageScroll?: boolean) => void;
  setActiveDateString: (dateString: string | undefined) => void;
}

/**
 * Hook to sync horizontal calendar with scrollable event list
 * Detects which date section is currently in view and provides scroll-to-date functionality
 */
export const useScrollSync = (options: ScrollSyncOptions = {}, initialDate?: string): ScrollSyncResult => {
  const { offsetTop = 150, throttleMs = 100 } = options;
  const [activeDateString, setActiveDateString] = useState(initialDate);
  const scrollTimeoutRef = useRef<number>();
  const isScrollingToDateRef = useRef(false);

  const findVisibleDate = useCallback(() => {
    if (isScrollingToDateRef.current) {
      return;
    }

    // Find all date section elements (they should have data-date attributes)
    const dateElements = document.querySelectorAll<HTMLElement>("[data-date]");

    if (dateElements.length === 0) {
      return;
    }

    const viewportTop = window.scrollY + offsetTop;
    let closestElement: HTMLElement | null = null;
    let closestDistance = Infinity;

    // Find the date section closest to our offset point
    for (const element of dateElements) {
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + window.scrollY;
      const distance = Math.abs(elementTop - viewportTop);

      if (distance < closestDistance && rect.top <= offsetTop) {
        closestDistance = distance;
        closestElement = element;
      }
    }

    if (closestElement) {
      const dateString = closestElement.dataset["date"];
      if (dateString) {
        setActiveDateString(dateString);
      }
    }
  }, [offsetTop]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = window.setTimeout(() => {
      findVisibleDate();
    }, throttleMs);
  }, [findVisibleDate, throttleMs]);

  const scrollToDate = useCallback(
    (dateString: string, skipPageScroll = false) => {
      if (skipPageScroll) {
        setActiveDateString(dateString);
        return;
      }

      const element = document.querySelector(`[data-date="${CSS.escape(dateString)}"]`);

      if (element) {
        isScrollingToDateRef.current = true;

        const elementRect = element.getBoundingClientRect();
        const offsetPosition = elementRect.top + window.scrollY - offsetTop + 20; // Small buffer

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        setTimeout(() => {
          isScrollingToDateRef.current = false;
        }, 1000);
      }
    },
    [offsetTop],
  );

  useEffect(() => {
    findVisibleDate();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll, findVisibleDate]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    },
    [],
  );

  return {
    activeDateString,
    scrollToDate,
    setActiveDateString,
  };
};
